import api, { USE_MOCK, qs, apiUrl } from './apiClient';

/**
 * Signature requests, from both ends.
 *
 * The `public*` calls are the ones an external counterparty makes: no session, identity
 * established by the emailed token plus a one-time code. They are grouped here rather than
 * in a separate module so it stays obvious which calls are reachable without signing in.
 *
 * Mock mode returns empty state. A demo that shows a contract as signed by somebody is a
 * claim about a person, and not one to invent.
 */
export const signatureService = {
  /** Requests raised against a document, for the panel on its page. */
  forDocument(documentId) {
    if (USE_MOCK) return Promise.resolve([]);
    return api.get(`/documents/${documentId}/signatures`);
  },

  create(documentId, body) {
    if (USE_MOCK) return Promise.resolve({ id: null, signerCount: 0 });
    return api.post(`/documents/${documentId}/signatures`, body);
  },

  cancel(requestId) {
    if (USE_MOCK) return Promise.resolve({ cancelled: true });
    return api.post(`/signature-requests/${requestId}/cancel`, {});
  },

  /** A colleague signing from inside the app: the session is the identity, so no code. */
  sign(requestId, mark) {
    if (USE_MOCK) return Promise.resolve({ signed: true, completed: false });
    return api.post(`/signature-requests/${requestId}/sign`, mark);
  },

  /** Colleagues who can already open this document — the signer picker. */
  audience(documentId, q) {
    if (USE_MOCK) return Promise.resolve([]);
    return api.get(`/documents/${documentId}/audience${qs({ q: q || undefined })}`);
  },

  /** Documents waiting on my signature. */
  mine() {
    if (USE_MOCK) return Promise.resolve([]);
    return api.get('/signature-requests/mine');
  },

  declineInApp(requestId, reason) {
    if (USE_MOCK) return Promise.resolve({ declined: true });
    return api.post(`/signature-requests/${requestId}/decline`, { reason });
  },

  /* ------------------------------------- the signature a person keeps */

  mySignature() {
    if (USE_MOCK) return Promise.resolve({ hasSignature: false, image: null });
    return api.get('/users/me/signature');
  },

  /** `image` is a data URL from a file input — a photo or scan. */
  saveMySignature(image) {
    if (USE_MOCK) return Promise.resolve({ hasSignature: true, image });
    return api.put('/users/me/signature', { image });
  },

  removeMySignature() {
    if (USE_MOCK) return Promise.resolve();
    return api.delete('/users/me/signature');
  },

  /* ------------------------------------------------- the signing page */

  publicOpen(token) {
    return api.get(`/public/signatures/${token}`);
  },

  publicStartVerification(token) {
    return api.post(`/public/signatures/${token}/verify-start`, {});
  },

  publicVerify(token, code) {
    return api.post(`/public/signatures/${token}/verify`, { code });
  },

  publicSign(token, mark) {
    return api.post(`/public/signatures/${token}/sign`, mark);
  },

  publicDecline(token, reason) {
    return api.post(`/public/signatures/${token}/decline`, { reason });
  },

  /** The PDF itself is fetched by the browser, not through the JSON client. */
  publicDocumentUrl(token) {
    return apiUrl(`/public/signatures/${token}/document`);
  },
};

export default signatureService;
